import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Loader2, Plus, RotateCcw } from 'lucide-react'
import type { BannerEngineConfig } from '@magnetar/admin-api-interfaces'
import { PageHeader } from '@/components/PageHeader'
import { DeleteAlert } from '@/components/DeleteAlert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/useAuth'
import { useBannerConfig } from '@/hooks/useBannerConfig'
import { useProviders } from '@/hooks/useProviders'
import { bannerConfigService } from '@/services/banner_config_service'
import { useViewMode } from '@/stores/viewMode'
import { formatDateTime } from '@/lib/datetime'
import { ConfigFields, draftFrom, draftToBody, draftValid, type ConfigDraft } from './ConfigFields'

interface ConfigCardProps {
  title: string
  description: string
  config: BannerEngineConfig
  providerId: number | null
  avgImageDurationMs: number
  onSaved: () => void
  extraActions?: React.ReactNode
}

function ConfigCard({ title, description, config, providerId, avgImageDurationMs, onSaved, extraActions }: ConfigCardProps) {
  const { token } = useAuth()
  const [draft, setDraft] = useState<ConfigDraft>(() => draftFrom(config))
  const [saving, setSaving] = useState(false)
  useEffect(() => setDraft(draftFrom(config)), [config])

  const handleSave = async () => {
    if (!token || !draftValid(draft)) return
    setSaving(true)
    const result = await bannerConfigService.save({ provider_id: providerId, ...draftToBody(draft) }, token)
    setSaving(false)
    if (result.success) {
      toast.success(result.data.warnings.length ? '已儲存（批長可能超過間隔，已提示）' : '已儲存')
      onSaved()
    } else {
      toast.error(result.error?.message || '儲存失敗')
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base">{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          {config.inherited ? (
            <Badge variant="outline">{providerId === null ? '內建預設，還沒存過' : '沿用全域'}</Badge>
          ) : (
            <Badge variant="secondary">更新於 {formatDateTime(config.updated_at)}</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <ConfigFields idPrefix={providerId === null ? 'global' : `p${providerId}`} draft={draft} onChange={setDraft} avgImageDurationMs={avgImageDurationMs} />
        <div className="flex justify-end gap-2">
          {extraActions}
          <Button onClick={handleSave} disabled={saving || !draftValid(draft)}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {providerId === null ? '儲存全域預設' : '儲存這個組織的設定'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

interface OverrideDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  base: BannerEngineConfig
  existing: (BannerEngineConfig & { provider_name: string }) | null
  takenProviderIds: number[]
  avgImageDurationMs: number
  onSaved: () => void
}

function OverrideDialog({ open, onOpenChange, base, existing, takenProviderIds, avgImageDurationMs, onSaved }: OverrideDialogProps) {
  const { token } = useAuth()
  const { providers } = useProviders({ page: 1, pageSize: 100, is_active: true })
  const [providerId, setProviderId] = useState<string>('')
  const [draft, setDraft] = useState<ConfigDraft>(() => draftFrom(base))
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setProviderId(existing ? String(existing.provider_id) : '')
      setDraft(draftFrom(existing ?? base))
      setSaving(false)
    }
  }, [open, existing, base])

  const candidates = (providers ?? []).filter(
    (p) => existing?.provider_id === p.provider_id || !takenProviderIds.includes(p.provider_id),
  )

  const handleSave = async () => {
    if (!token || !providerId || !draftValid(draft)) return
    setSaving(true)
    const result = await bannerConfigService.save({ provider_id: Number(providerId), ...draftToBody(draft) }, token)
    setSaving(false)
    if (result.success) {
      toast.success('已儲存組織覆蓋')
      onOpenChange(false)
      onSaved()
    } else {
      toast.error(result.error?.message || '儲存失敗')
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{existing ? `編輯 ${existing.provider_name} 的覆蓋` : '新增組織覆蓋'}</DialogTitle>
          <DialogDescription>組織有自己的設定時就不沿用全域；移除覆蓋會回到全域預設。</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="override-provider">組織</Label>
            <Select value={providerId} onValueChange={setProviderId} disabled={existing !== null}>
              <SelectTrigger id="override-provider" aria-label="組織">
                <SelectValue placeholder="選組織" />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((p) => (
                  <SelectItem key={p.provider_id} value={String(p.provider_id)}>
                    {p.provider_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <ConfigFields idPrefix="override" draft={draft} onChange={setDraft} avgImageDurationMs={avgImageDurationMs} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            取消
          </Button>
          <Button onClick={handleSave} disabled={saving || !providerId || !draftValid(draft)}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            儲存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Banner 引擎配置（站點地圖 admin /banner-config；CR-1）：全域預設＋組織覆蓋，未設則繼承 */
export default function BannerConfigPage() {
  const { token } = useAuth()
  const { mode, id: viewModeId } = useViewMode()
  const providerId = mode === 'provider' && viewModeId ? viewModeId : undefined
  const { config, isLoading, isError, mutate } = useBannerConfig(providerId)
  const [dialog, setDialog] = useState<{ existing: (BannerEngineConfig & { provider_name: string }) | null } | null>(null)
  const [removing, setRemoving] = useState<number | null>(null)

  const handleRemove = async () => {
    if (!token || removing === null) return
    const result = await bannerConfigService.removeOverride(removing, token)
    if (result.success) {
      toast.success('已移除，回到沿用全域')
      mutate()
    } else {
      toast.error(result.error?.message || '移除失敗')
    }
    setRemoving(null)
  }

  if (isError) {
    return <div className="py-12 text-center text-destructive">載入 Banner 引擎配置失敗，請稍後再試。</div>
  }
  if (isLoading || !config) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  const avg = config.avg_image_duration_ms

  return (
    <div className="space-y-6">
      <PageHeader
        title="Banner 引擎配置"
        description="每小時幾批、每批幾張、多久觸發一次。廣告商看不到這裡；圖的「N 批內次數」以循環批數為分母。"
        level={mode === 'admin' ? 'page' : 'section'}
      />

      {providerId && config.effective ? (
        <ConfigCard
          title="這個組織的設定"
          description={config.effective.inherited ? '目前沿用全域預設；存檔後就是這個組織自己的設定。' : '這個組織有自己的設定，不沿用全域。'}
          config={config.effective}
          providerId={providerId}
          avgImageDurationMs={avg}
          onSaved={() => mutate()}
          extraActions={
            !config.effective.inherited ? (
              <Button variant="outline" onClick={() => setRemoving(providerId)}>
                <RotateCcw className="mr-2 h-4 w-4" />
                回到沿用全域
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <ConfigCard
            title="全域預設"
            description="沒有自己設定的組織一律沿用這裡。"
            config={config.global}
            providerId={null}
            avgImageDurationMs={avg}
            onSaved={() => mutate()}
          />
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <CardTitle className="text-base">組織覆蓋（{config.overrides.length}）</CardTitle>
                  <CardDescription>有自己設定的組織；其餘組織沿用全域。</CardDescription>
                </div>
                <Button onClick={() => setDialog({ existing: null })}>
                  <Plus className="mr-2 h-4 w-4" />
                  新增覆蓋
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {config.overrides.length === 0 ? (
                <p className="text-sm text-muted-foreground">還沒有組織覆蓋，所有組織都沿用全域預設。</p>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>組織</TableHead>
                        <TableHead className="text-right">循環批數</TableHead>
                        <TableHead className="text-right">每批槽位</TableHead>
                        <TableHead className="text-right">觸發間隔</TableHead>
                        <TableHead>更新</TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {config.overrides.map((o) => (
                        <TableRow key={o.provider_id ?? 'global'}>
                          <TableCell className="font-medium">{o.provider_name}</TableCell>
                          <TableCell className="text-right tabular-nums">{o.cycle_count}</TableCell>
                          <TableCell className="text-right tabular-nums">{o.per_cycle_capacity}</TableCell>
                          <TableCell className="text-right tabular-nums">{Math.round(o.trigger_interval_ms / 1000)} 秒</TableCell>
                          <TableCell className="tabular-nums">{formatDateTime(o.updated_at)}</TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-2">
                              <Button variant="outline" size="sm" onClick={() => setDialog({ existing: o })}>
                                編輯
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() => o.provider_id !== null && setRemoving(o.provider_id)}
                              >
                                移除
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
          <OverrideDialog
            open={dialog !== null}
            onOpenChange={(open) => !open && setDialog(null)}
            base={config.global}
            existing={dialog?.existing ?? null}
            takenProviderIds={config.overrides.map((o) => o.provider_id).filter((id): id is number => id !== null)}
            avgImageDurationMs={avg}
            onSaved={() => mutate()}
          />
        </>
      )}

      <p className="text-xs text-muted-foreground">
        提示口徑：一批要播的秒數＝每批槽位 × 平均已核准圖時長（目前 {Math.round(avg / 1000)} 秒），超過觸發間隔只提醒、不擋存檔。
      </p>

      <DeleteAlert
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        onConfirm={handleRemove}
        title="移除這個組織的設定？"
        description="移除後這個組織會回到沿用全域預設，下一輪排批立即生效。"
      />
    </div>
  )
}
